import { InputProfilePicture } from "@/components";
import { BackAppBar } from "@/components/ui";
import useProfile from "@/hooks/useProfile";
import { preSignAvatar } from "@/redux/reducers/auth";
import EditIcon from "@mui/icons-material/Edit";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import WarningIcon from "@mui/icons-material/Warning";
import Badge from "@mui/material/Badge";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { amber } from "tailwindcss/colors";

export const Route = createFileRoute("/dashboard/accounts/settings/profile/")({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation(["settings", "nav"]);
	const dispatch = useDispatch();
	const { profile: userData } = useProfile();

	const { register, watch } = useForm<Record<"avatar", FileList | string>>({
		mode: "onChange",
		defaultValues: async () => ({
			avatar: userData?.avatar ?? "",
		}),
	});

	const avatar = watch("avatar");
	const avatarUrl = useMemo(() => {
		if (!avatar || !avatar?.[0]) return undefined;
		if (typeof avatar === "string") {
			return avatar;
		}
		return URL.createObjectURL(avatar?.[0]);
	}, [avatar]);

	const isComplete = useMemo(
		() =>
			[
				userData?.avatar,
				userData?.job_title,
				userData?.qualification,
				userData?.spesialization,
				userData?.country,
				userData?.profession,
				userData?.fullname,
				userData?.hospital?.name,
				userData?.registration_number,
			].every(Boolean),
		[
			userData?.avatar,
			userData?.country,
			userData?.fullname,
			userData?.registration_number,
			userData?.hospital?.name,
			userData?.job_title,
			userData?.profession,
			userData?.qualification,
			userData?.spesialization,
		],
	);

	const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.item(0);
		if (file) {
			dispatch(preSignAvatar({ userId: userData?.id, file }));
		}
	};

	return (
		<div className="wrapper !p-0 min-h-full mx-auto">
			<BackAppBar
				to="/account"
				className="!bg-gradient-account [&_*_]:text-white"
				titleKey="profileSettings"
			>
				{!isComplete ? (
					<Stack
						direction="row"
						spacing={2}
						alignItems="center"
						sx={{ backgroundColor: amber[100], px: "1rem", py: 1 }}
					>
						<WarningIcon
							color="warning"
							fontSize="small"
							className="!fill-amber-600"
						/>
						<p className="font-normal text-sm !text-dark">
							{t("settings:profile.incompleteWarning")}
						</p>
					</Stack>
				) : null}
			</BackAppBar>
			<div className="container bg-gray-light">
				<Stack spacing={2} alignItems="center" py={2}>
					<InputProfilePicture
						image={avatarUrl}
						{...register("avatar", {
							onChange: handleAvatarChange,
						})}
					/>
					<Card variant="outlined" className="w-full !rounded-xl">
						<CardHeader
							avatar={<Badge />}
							title={
								<p className="font-normal text-sm">
									{t("settings:profile.personalInformation")}
								</p>
							}
							action={
								<Link to="/dashboard/accounts/settings/profile/credentials">
									<IconButton>
										<EditIcon />
									</IconButton>
								</Link>
							}
							className="border-b border-zinc-200"
						/>
						<CardContent>
							<Stack spacing={2}>
								<Stack spacing={1}>
									<p className="font-normal text-sm">
										{t("settings:profile.country")}
									</p>
									<p className="font-medium text-sm">
										{userData?.country ?? "-"}
									</p>
								</Stack>
								<Stack spacing={1}>
									<p className="font-normal text-sm">
										{t("settings:profile.profession")}
									</p>
									<p className="font-medium text-sm">
										{userData?.profession ?? "-"}
									</p>
								</Stack>
								<Stack spacing={1}>
									<p className="font-normal text-sm">
										{t("settings:profile.fullNameAsId")}
									</p>
									<p className="font-medium text-sm">
										{userData?.fullname ?? "-"}
									</p>
								</Stack>
								<Stack spacing={1}>
									<p className="font-normal text-sm">
										{t("settings:profile.workplace")}
									</p>
									<p className="font-medium text-sm">
										{userData?.hospital?.name ?? "-"}
									</p>
								</Stack>
								<Stack spacing={1}>
									<p className="font-normal text-sm">
										{t("settings:profile.registrationNumberLabel")}
									</p>
									<p className="font-medium text-sm">
										{userData?.registration_number ?? "-"}
									</p>
								</Stack>
							</Stack>
						</CardContent>
					</Card>
					<Card variant="outlined" className="w-full !rounded-xl">
						<CardHeader
							avatar={<TextSnippetIcon />}
							title={
								<p className="font-normal text-sm">
									{t("settings:profile.additionalInfoDirectory")}
								</p>
							}
							action={
								<Link to="/dashboard/accounts/settings/profile/information">
									<IconButton>
										<EditIcon />
									</IconButton>
								</Link>
							}
							className="border-b border-zinc-200"
						/>
						<CardContent>
							<Stack spacing={2}>
								<Stack spacing={1}>
									<p className="font-normal text-sm">
										{t("settings:profile.jobTitle")}
									</p>
									<p className="font-medium text-sm">
										{userData?.job_title ?? "-"}
									</p>
								</Stack>
								<Stack spacing={1}>
									<p className="font-normal text-sm">
										{t("settings:profile.qualifications")}
									</p>
									<p className="font-medium text-sm">
										{userData?.qualification ?? "-"}
									</p>
								</Stack>
								<Stack spacing={1}>
									<p className="font-normal text-sm">
										{t("settings:profile.specialisation")}
									</p>
									<p className="font-medium text-sm">
										{userData?.spesialization ?? "-"}
									</p>
								</Stack>
								<Stack spacing={1}>
									<p className="font-normal text-sm">
										{t("settings:profile.addToDirectory")}
									</p>
									<p className="font-medium text-sm">
										{!userData?.directory
											? t("settings:profile.no")
											: t("settings:profile.yes")}
									</p>
								</Stack>
							</Stack>
						</CardContent>
					</Card>
				</Stack>
			</div>
		</div>
	);
}
